import React from 'react'
import { X } from 'lucide-react'
import { Button, Loader } from 'components/ui'
import { classNames } from '../classNames'
import styles from './styles.module.css'

export interface MediaUploadPreviewProps {
  /** Resolved display URL. URL validation and upload state belong to the caller. */
  src?: string
  mediaType: 'image' | 'video' | 'file'
  /** Static artwork for file previews; upload and remove controls stay shared with photos. */
  previewContent?: React.ReactNode
  alt: string
  previewLabel: string
  uploadingLabel: string
  uploading?: boolean
  error?: boolean
  errorLabel?: string
  onPreview?: () => void
  href?: string
  previewDisabled?: boolean
  onMediaError?: () => void
  /** Interactive decorations live outside the preview button. */
  overlay?: React.ReactNode
  removeAction?: { label: string; onClick: () => void; disabled?: boolean }
  className?: string
  previewClassName?: string
  style?: React.CSSProperties
  title?: string
}

/** Shared thumbnail: preserve the media node while an upload moves from blurred to ready. */
export const MediaUploadPreview: React.FC<MediaUploadPreviewProps> = ({
  src,
  mediaType,
  previewContent,
  alt,
  previewLabel,
  uploadingLabel,
  uploading = false,
  error = false,
  errorLabel,
  onPreview,
  href,
  previewDisabled = false,
  onMediaError,
  overlay,
  removeAction,
  className,
  previewClassName,
  style,
  title,
}) => {
  const busy = uploading && !error
  const link = href && !onPreview && !previewDisabled ? href : undefined
  const disabled = previewDisabled || (!onPreview && !link)

  return (
    <div
      className={classNames(styles.root, busy && styles.isUploading, className)}
      style={style}
      title={title}
      aria-busy={busy}
    >
      <Button
        as={link ? 'a' : 'button'}
        type={link ? undefined : 'button'}
        href={link}
        target={link ? '_blank' : undefined}
        rel={link ? 'noopener noreferrer' : undefined}
        onClick={onPreview}
        isDisabled={disabled}
        variant="ghost"
        aria-label={previewLabel}
        className={classNames(styles.preview, previewClassName)}
        _disabled={{ opacity: 1, cursor: 'default' }}
      >
        {mediaType === 'file' && <span className={styles.content}>{previewContent}</span>}
        {mediaType !== 'file' &&
          src &&
          (mediaType === 'video' ? (
            <video src={`${src}#t=0.1`} muted playsInline preload="metadata" aria-label={alt} onError={onMediaError} />
          ) : (
            <img src={src} alt={alt} onError={onMediaError} />
          ))}
        {busy && (
          <span className={styles.uploading} role="status" aria-label={uploadingLabel}>
            <Loader size="md" color="#FFFFFF" h="100%" />
          </span>
        )}
        {error && errorLabel && <span className={styles.error}>{errorLabel}</span>}
      </Button>
      {overlay}
      {removeAction && (
        <Button
          type="button"
          variant="icon"
          className={styles.remove}
          aria-label={removeAction.label}
          onClick={removeAction.onClick}
          isDisabled={removeAction.disabled}
        >
          <X size={13} strokeWidth={2} />
        </Button>
      )}
    </div>
  )
}
